import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ListOption } from '@/components/list-item';
import { ScrollView, ScrollViewProps } from 'react-native';
import { useDebouncedCallback } from 'use-debounce';

export interface PositionType {
  startPosition: number;
}

export type TabListOption = ListOption<PositionType>;

interface ScrollViewTabsContextType {
  onScroll: ScrollViewProps['onScroll'];
  onTabPress: (tabOption: TabListOption | null) => void;
  registerTab: (name: string, yPosition: number) => void;
  scrollOffset: number;
  scrollViewRef: React.RefObject<ScrollView | null>;
  selectedTabOption: TabListOption | null;
  tabOptions: TabListOption[];
}

export const ScrollViewTabsContext = createContext<ScrollViewTabsContextType>({
  onScroll: () => {},
  onTabPress: () => {},
  registerTab: () => {},
  scrollOffset: 0,
  scrollViewRef: { current: null },
  selectedTabOption: null,
  tabOptions: [],
});

export const useScrollViewTabsContext = () => {
  return useContext(ScrollViewTabsContext);
};

interface Props {
  children: React.ReactNode;
  scrollOffset?: number;
}

// changes to what gets rendered in the scroll view can re-trigger onLayout events
// Depending on the order of the events, tabs can appear out of order until the next is registered
// Delay the update so all views can register their new positions
const UPDATE_OPTIONS_DELAY_MS = 200;

export const ScrollViewTabsContextProvider: React.FC<Props> = ({
  children,
  scrollOffset = 0,
}) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [registeredTabs, setRegisteredTabs] = useState<
    Map<string, TabListOption>
  >(new Map<string, TabListOption>());
  const scrollRef = useRef<ScrollView>(null);
  const [tabOptions, setTabOptions] = useState<TabListOption[]>([]);

  const debouncedSetTabOptions = useDebouncedCallback(
    (registeredTabMap: Map<string, TabListOption>) => {
      const registered = Array.from(registeredTabMap.values());
      setTabOptions(
        registered.sort(
          (a, b) => a.metadata!.startPosition - b.metadata!.startPosition,
        ),
      );
    },
    UPDATE_OPTIONS_DELAY_MS,
  );

  useEffect(() => {
    debouncedSetTabOptions(registeredTabs);
    // only re-run when registeredTabs changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registeredTabs]);

  const selectedTabOption = useMemo(() => {
    // prevent vertical bounce from selecting last option
    if (scrollPosition <= 0) {
      return tabOptions[0];
    }

    for (let i = 0; i < tabOptions.length - 1; i++) {
      const startPosition = i === 0 ? 0 : tabOptions[i].metadata!.startPosition;
      const endPosition =
        i === tabOptions.length - 1
          ? Infinity
          : tabOptions[i + 1].metadata!.startPosition;
      const headerOption = tabOptions[i];
      if (startPosition <= scrollPosition && scrollPosition < endPosition) {
        return headerOption;
      }
    }
    return tabOptions[tabOptions.length - 1];
  }, [scrollPosition, tabOptions]);

  const value = useMemo<ScrollViewTabsContextType>(() => {
    return {
      onScroll: (e) => {
        setScrollPosition(e.nativeEvent.contentOffset.y);
      },
      onTabPress: (tabOption: TabListOption | null) => {
        if (!tabOption) {
          return;
        }
        scrollRef.current?.scrollTo({
          animated: true,
          y: tabOption.metadata!.startPosition,
        });
      },
      registerTab: (name, yPosition) => {
        setRegisteredTabs((prev) => {
          const newTabs = new Map(prev);
          newTabs.set(name, {
            metadata: {
              startPosition: yPosition - scrollOffset,
            },
            title: name,
            value: name,
          });
          return newTabs;
        });
      },
      scrollOffset,
      scrollViewRef: scrollRef,
      selectedTabOption,
      tabOptions,
    };
  }, [scrollOffset, selectedTabOption, tabOptions]);

  return (
    <ScrollViewTabsContext.Provider value={value}>
      {children}
    </ScrollViewTabsContext.Provider>
  );
};
